import type { Ref } from 'vue';
import type { GridLayoutData, RowBounds } from './grid-types';
import { ref, watch } from 'vue';

export interface GridBoundsState {
	containerBounds: Ref<DOMRect | null>;
	rowBounds: Ref<Record<number, RowBounds>>;
	lockedRowHeights: Ref<Record<number, number>>;
}

/**
 * Composable for tracking grid container and row bounds
 */
export function useGridBounds(
	gridData: Ref<GridLayoutData>,
	containerRef: Ref<HTMLElement | null>,
	rowRefs: Ref<Record<number, HTMLElement>>,
	isActive: Ref<boolean>,
): GridBoundsState & { updateBounds: () => void } {
	const containerBounds = ref<DOMRect | null>(null);
	const rowBounds = ref<Record<number, RowBounds>>({});
	const lockedRowHeights = ref<Record<number, number>>({});

	function updateBounds() {
		if (containerRef.value) {
			containerBounds.value = containerRef.value.getBoundingClientRect();
		}

		const newRowBounds: Record<number, RowBounds> = {};

		for (const [index, element] of Object.entries(rowRefs.value)) {
			const rowIndex = Number(index);
			const rect = element.getBoundingClientRect();
			const row = gridData.value.rows[rowIndex];

			if (row) {
				newRowBounds[rowIndex] = {
					top: rect.top,
					left: rect.left,
					width: rect.width,
					height: rect.height,
					columns: row.columns,
				};

				if (isActive.value && lockedRowHeights.value[rowIndex] === undefined) {
					lockedRowHeights.value[rowIndex] = rect.height;
				}
			}
		}

		rowBounds.value = newRowBounds;
	}

	watch(isActive, (active) => {
		if (active) {
			updateBounds();
		}
		else {
			lockedRowHeights.value = {};
		}
	});

	return {
		containerBounds,
		rowBounds,
		lockedRowHeights,
		updateBounds,
	};
}

/**
 * Calculate style for positioning an element at a specific grid position
 */
export function calculateGridPositionStyle(
	rowIndex: number,
	colStart: number,
	colSpan: number,
	rowBounds: Record<number, RowBounds>,
	containerBounds: DOMRect | null,
): Record<string, string> {
	const bounds = rowBounds[rowIndex];

	if (!bounds || !containerBounds) {
		return {};
	}

	const cellWidth = bounds.width / bounds.columns;
	const left = bounds.left - containerBounds.left + colStart * cellWidth;
	const width = colSpan * cellWidth;

	return {
		position: 'absolute',
		top: `${bounds.top - containerBounds.top}px`,
		left: `${left}px`,
		width: `${width}px`,
		height: `${bounds.height}px`,
	};
}

/**
 * Calculate style for a single column guide
 */
export function calculateColumnGuideStyle(
	rowIndex: number,
	colIndex: number,
	rowBounds: Record<number, RowBounds>,
	containerBounds: DOMRect | null,
): Record<string, string> {
	return calculateGridPositionStyle(rowIndex, colIndex, 1, rowBounds, containerBounds);
}
